import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

interface PageContextState {
  pageData: string | null
  setPageSection: (key: string, value: string | null) => void
}

const PageContext = createContext<PageContextState | null>(null)

/** Reported in the page context when a page failed, in the words the advisor sees. */
export const PAGE_NOT_LOADED = 'Page could not be loaded'

export const PageContextProvider = ({ children }: { children: ReactNode }) => {
  const [sections, setSections] = useState<Record<string, string>>({})

  const setPageSection = useCallback((key: string, value: string | null) => {
    setSections((prev) => {
      if (value === null) {
        const { [key]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [key]: value }
    })
  }, [])

  const pageData = Object.values(sections).join('\n') || null
  const value = useMemo(() => ({ pageData, setPageSection }), [pageData, setPageSection])
  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}

/**
 * Returns the combined page context string and the section setter.
 * Must be used within PageContextProvider.
 */
export const usePageContextStore = (): PageContextState => {
  const ctx = useContext(PageContext)
  if (!ctx) throw new Error('usePageContextStore must be used within PageContextProvider')
  return ctx
}
